/**
 * Assurance-tier obligations, client side.
 *
 * A mirror of `tierRequiresAccessCode` / `tierRequiresPreConsentAccess` in
 * `@fias/db-types`, which `@fias/webapp-client` deliberately does not depend
 * on. The same situation as the plugin CLI's `validateSiteBlock` and the host
 * bridge's inlined arche-id check: mirrored rather than imported, and pinned so
 * the copy cannot drift.
 *
 * `tests/architecture/document-sign-assurance-tier.test.ts` reads this file and
 * asserts it agrees with the canonical helpers. **Change both together.**
 *
 * Why a helper at all rather than `tier === 'regulated'` at each call site:
 * the tier's obligations are policy (FIAS-ESTE-009 §2.3), and raising a floor
 * has to be one edit in one place that CI can see. Scattered literals are how
 * the UI ends up promising something the server does not enforce, or the
 * reverse.
 */

import type { DocumentSignAssuranceTier } from '@/lib/api';

/** Does this tier require an access code for every signer? */
export function tierRequiresAccessCode(tier: DocumentSignAssuranceTier): boolean {
  return tier === 'regulated';
}

/**
 * Does this tier require the signer to demonstrate access to the delivery
 * format before consenting (15 U.S.C. § 7001(c)(1)(C)(ii))?
 */
export function tierRequiresPreConsentAccess(tier: DocumentSignAssuranceTier): boolean {
  return tier === 'regulated';
}

/**
 * Whether the signing screen offers an explicit "request a paper copy" choice.
 *
 * Tracks § 7001(c)(1)(B)(i) — the disclosed right to withdraw consent and
 * obtain paper — which engages only where a law requires a consumer to receive
 * something in writing. Kept separate from the other two helpers so a future
 * change to one does not silently move the others.
 */
export function tierOffersPaperCopyElection(tier: DocumentSignAssuranceTier): boolean {
  return tier === 'regulated';
}
